import { useTranslation } from 'react-i18next';
import type { PlatformCategoryCount } from '@/api/platformMetrics/platformMetricsApi.types';
import { ChartCard } from '@/components/common/charts/ChartCard';
import { DonutChart, type DonutSlice } from '@/components/common/charts/DonutChart';
import { usePlatformBreakdownQuery } from '@/hooks/usePlatformChartsQueries';
import { WORKSPACE_STATUS_COLORS, resolveSeriesColor } from '@/theme/chartPalette';

interface WorkspaceStatusChartProps {
  isEnabled: boolean;
}

export function WorkspaceStatusChart({ isEnabled }: WorkspaceStatusChartProps) {
  const { t } = useTranslation('admin');
  const breakdownQuery = usePlatformBreakdownQuery(isEnabled);
  const buckets = breakdownQuery.data === undefined ? [] : breakdownQuery.data.byStatus;
  const slices = buckets.map(function toSlice(bucket, index) {
    return toStatusSlice(bucket, index, t);
  });
  const total = slices.reduce(function addValue(sum, slice) {
    return sum + slice.value;
  }, 0);

  return (
    <ChartCard
      title={t('dashboard.charts.status.title')}
      iconName="PieChart"
      isLoading={breakdownQuery.isLoading === true}
      isError={breakdownQuery.isError === true}
      onRetry={function retry() {
        breakdownQuery.refetch();
      }}
      isEmpty={total === 0}
      emptyLabel={t('dashboard.charts.status.empty')}
    >
      <DonutChart
        data={slices}
        accessibleLabel={t('dashboard.charts.status.title')}
        formatValue={formatWholeNumber}
      />
    </ChartCard>
  );
}

function toStatusSlice(
  bucket: PlatformCategoryCount,
  index: number,
  translate: (key: string, options: { defaultValue: string }) => string
): DonutSlice {
  const color = WORKSPACE_STATUS_COLORS[bucket.category];
  return {
    key: bucket.category,
    label: translate('dashboard.charts.status.' + bucket.category, { defaultValue: bucket.category }),
    value: bucket.count,
    color: color === undefined ? resolveSeriesColor(index) : color,
  };
}

function formatWholeNumber(value: number): string {
  return String(value);
}
